import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Plus } from "lucide-react";

const emptyPrestation = { description: "", prix: "", quantite: "" };

const statusOptions = [
  { value: "pending", labelKey: "invoice.statusPending" },
  { value: "paid", labelKey: "invoice.statusPaid" },
  { value: "canceled", labelKey: "invoice.statusCanceled" },
];

const InvoiceForm = ({ invoice, onSave, onCancel }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    quote_id: "",
    client_name: "",
    client_address: "",
    client_city: "",
    client_zip: "",
    client_phone: "",
    client_email: "",
    client_siret: "",
    date: new Date().toISOString().slice(0, 10),
    prestations: [{ ...emptyPrestation }],
    status: "pending",
  });

  useEffect(() => {
    if (invoice) {
      setForm({
        quote_id: invoice.quote_id ?? "",
        client_name: invoice.client_name ?? "",
        client_address: invoice.client_address ?? "",
        client_city: invoice.client_city ?? "",
        client_zip: invoice.client_zip ?? "",
        client_phone: invoice.client_phone ?? "",
        client_email: invoice.client_email ?? "",
        client_siret: invoice.client_siret ?? "",
        date: invoice.date ?? new Date().toISOString().slice(0, 10),
        prestations:
          Array.isArray(invoice.prestations) && invoice.prestations.length > 0
            ? invoice.prestations
            : [{ ...emptyPrestation }],
        status: invoice.status ?? "pending",
      });
    }
  }, [invoice]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePrestationChange = (idx, e) => {
    const next = form.prestations.map((p, i) =>
      i === idx ? { ...p, [e.target.name]: e.target.value } : p
    );
    setForm({ ...form, prestations: next });
  };

  const addPrestation = () =>
    setForm({ ...form, prestations: [...form.prestations, { ...emptyPrestation }] });

  const removePrestation = (idx) =>
    setForm({ ...form, prestations: form.prestations.filter((_, i) => i !== idx) });

  // Calcul des totaux
  const totalHT = form.prestations.reduce(
    (sum, p) => sum + (parseFloat(p.prix || 0) * parseFloat(p.quantite || 0)),
    0
  );
  const totalTVA = +(totalHT * 0.2).toFixed(2);
  const totalTTC = +(totalHT + totalTVA).toFixed(2);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      prestations: form.prestations.filter((p) => p.description && p.prix && p.quantite),
      totalHT: totalHT.toFixed(2),
      totalTVA: totalTVA.toFixed(2),
      totalTTC: totalTTC.toFixed(2),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">

      {/* Section : Informations client */}
      <div className="bg-base-200/50 rounded-xl p-4 border border-base-200 space-y-4">
        <h3 className="text-xs font-bold text-base-content/50 uppercase tracking-widest">
          {t("invoice.clientName")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientName")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_name"
              value={form.client_name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientEmail")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_email"
              value={form.client_email}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientAddress")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_address"
              value={form.client_address}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientCity")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_city"
              value={form.client_city}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientZip")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_zip"
              value={form.client_zip}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientPhone")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_phone"
              value={form.client_phone}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.clientSiret")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="client_siret"
              value={form.client_siret}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      {/* Section : Paramètres facture */}
      <div className="bg-base-200/50 rounded-xl p-4 border border-base-200 space-y-4">
        <h3 className="text-xs font-bold text-base-content/50 uppercase tracking-widest">
          {t("invoice.status")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.date")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="date"
              type="date"
              value={form.date}
              onChange={handleChange}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.status")}</label>
            <select
              className="select select-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-base-content/70">{t("invoice.quoteId")}</label>
            <input
              className="input input-bordered w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
              name="quote_id"
              value={form.quote_id || ""}
              onChange={handleChange}
              type="text"
              placeholder="—"
            />
          </div>
        </div>
      </div>

      {/* Section : Prestations */}
      <div className="bg-base-200/50 rounded-xl p-4 border border-base-200 space-y-3">
        <h3 className="text-xs font-bold text-base-content/50 uppercase tracking-widest">
          {t("invoice.prestations")}
        </h3>

        <div className="overflow-x-auto rounded-lg border border-base-200">
          <table className="table table-sm w-full">
            <thead>
              <tr className="bg-base-200 border-b border-base-200">
                <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-2.5">
                  {t("invoice.desc")}
                </th>
                <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-2.5 w-28">
                  {t("invoice.unitPrice")}
                </th>
                <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-2.5 w-20">
                  {t("invoice.qty")}
                </th>
                <th className="text-right text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-2.5 w-28">
                  {t("invoice.total")}
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-base-200">
              {form.prestations.map((p, idx) => (
                <tr key={idx} className="bg-base-100">
                  <td className="p-2">
                    <input
                      name="description"
                      className="input input-bordered input-sm w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
                      value={p.description}
                      onChange={(e) => handlePrestationChange(idx, e)}
                      required
                    />
                  </td>
                  <td className="p-2">
                    <input
                      name="prix"
                      type="number"
                      step="0.01"
                      className="input input-bordered input-sm w-full bg-base-200 border-base-300 text-base-content text-right focus:border-primary focus:outline-none"
                      value={p.prix}
                      onChange={(e) => handlePrestationChange(idx, e)}
                      required
                    />
                  </td>
                  <td className="p-2">
                    <input
                      name="quantite"
                      type="number"
                      min="1"
                      className="input input-bordered input-sm w-full bg-base-200 border-base-300 text-base-content text-right focus:border-primary focus:outline-none"
                      value={p.quantite}
                      onChange={(e) => handlePrestationChange(idx, e)}
                      required
                    />
                  </td>
                  <td className="p-2 text-right font-mono font-semibold text-base-content/70 text-sm">
                    {p.prix && p.quantite
                      ? (parseFloat(p.prix) * parseFloat(p.quantite)).toFixed(2) + " €"
                      : "—"}
                  </td>
                  <td className="p-2 text-center">
                    {form.prestations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePrestation(idx)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/40 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                        title={t("invoice.remove")}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          onClick={addPrestation}
          className="btn btn-ghost btn-sm gap-1.5 text-primary border border-primary/20 hover:bg-primary/10"
        >
          <Plus size={14} />
          {t("invoice.addPrestation")}
        </button>
      </div>

      {/* Totaux */}
      <div className="flex flex-wrap justify-end gap-6 px-4 py-3 bg-base-200/50 rounded-xl border border-base-200">
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-xs text-base-content/50 font-semibold uppercase tracking-widest">{t("invoice.totalHT")}</span>
          <span className="font-mono font-bold text-base-content">{totalHT.toFixed(2)} €</span>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-xs text-base-content/50 font-semibold uppercase tracking-widest">{t("invoice.tva")} (20%)</span>
          <span className="font-mono font-bold text-base-content/70">{totalTVA.toFixed(2)} €</span>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-xs text-base-content/50 font-semibold uppercase tracking-widest">{t("invoice.totalTTC")}</span>
          <span className="font-mono font-extrabold text-lg text-primary">{totalTTC.toFixed(2)} €</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
        >
          {t("invoice.cancel")}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
        >
          {t("invoice.save")}
        </button>
      </div>
    </form>
  );
};

export default InvoiceForm;
